import { Injectable, inject } from '@angular/core'
import DataSource from 'devextreme/data/data_source'
import { Observable, zip, from, of, throwError } from 'rxjs'
import { catchError, map, mergeMap, tap } from 'rxjs/operators'
import dayjs, { Dayjs } from 'dayjs'
import sameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import utc from 'dayjs/plugin/utc'
import { ChartConfig, ChartSeries, ChartValue, GenericChartItem, MetricResult, MetricValue } from 'src/app/types/chart.model'
import {
  ChartDataSource,
  DateRangeType,
  MAX_ROLLING_PERIODS,
  ValueAggregationType,
} from 'src/app/types/enums'
import { ClaimsApiService } from '../api/claims.api.service'
import { JobsApiService } from '../api/jobs.api.service'
import { TasksApiService } from '../api/tasks.api.service'
import { ActivitiesApiService } from '../api/activities.api.service'
import { DateUtilsService } from '../date-utils.service'
import { UtilsService } from '../utils.service'
import { JobTaskForecastsService } from '../api/job-task-forecasts.service'
import { GROUP_KEYS_FUNCTION, WarningNotifier } from 'src/app/types/constants'
import { LogService } from '../log.service'
import { AVERAGING_DECIMAL_PLACES_ROUNDING } from 'src/config/global-config'
import { ExtrasApiService } from '../api/extras.api.service'
import { JobFieldsApiService } from '../api/job-fields.api.service'
import { UserService } from '../api/user.api.service'
import { NotificationService } from '../notification.service'
import { TaskDurationService } from './task-duration.service'
import { DatasourceConfigService } from './datasource-config.service'
import { buildForecastTasksItemValueFunc, buildJobFieldValueFunc, CONTRACT_PRICE_SENTINEL, CONTRACT_TOTAL_EXC_GST_SENTINEL, TRACKING_VALUE_FIELD_SENTINEL } from 'src/app/types/datasources/forecast-tasks.model'
import { ChartDatasourceHelper } from './chart-datasource-helper.service'
import { ProcessedDashboardJob } from 'src/app/types/datasources/job.model'
import { ChartResult, FieldRankMaps, FilteredItems, ItemValueFunc, SERIES_EMPTY_TEXT } from './types'
import { ForecastIntervalWarningCollector, getDistinctOverlappingForecastTasks } from 'src/app/types/datasources/forecast-tasks-in-progress.model'
import { RecalcCompletionForecast } from 'src/app/types/dto/recalc-completion-forecast.dto'
import { ProcessedJobTask } from 'src/app/types/datasources/tasks.model'
import { ReportsApiService } from '../api/reports.api.service'
dayjs.extend(sameOrBefore)
dayjs.extend(isSameOrAfter)
dayjs.extend(utc)

interface ForecastDataContext {
  task1Items: RecalcCompletionForecast[]
  task2Items: RecalcCompletionForecast[]
  itemValueFunc?: ItemValueFunc
}

@Injectable({
  providedIn: 'root',
})
export class ChartDatasourceProviderService {
  private taskDurationService = inject(TaskDurationService);
  private datasourceConfig = inject(DatasourceConfigService);
  private tasksApi = inject(TasksApiService);
  private claimsApi = inject(ClaimsApiService);
  private jobsApi = inject(JobsApiService);
  private reportsApi = inject(ReportsApiService);
  private activitiesApi = inject(ActivitiesApiService);
  private cashFlowApi = inject(JobTaskForecastsService);
  private extrasApi = inject(ExtrasApiService);
  private jobFieldsApi = inject(JobFieldsApiService);
  private userService = inject(UserService);
  private dateUtils = inject(DateUtilsService);
  private logger = inject(LogService);
  private utils = inject(UtilsService);
  private notiService = inject(NotificationService);

  groupKeyFuncOverrideItems: object[] = []

  provideData(config: ChartConfig): Observable<ChartResult> {
    switch (config.dataSourceId) {
      case ChartDataSource.Tasks:
        return this.getTasksData(config)
      case ChartDataSource.TaskDurations:
        return this.getTaskDurationData(config)
      case ChartDataSource.Claims:
        return this.getClaimsData(config)
      case ChartDataSource.Jobs:
        return this.getJobsData(config)
      case ChartDataSource.JobsBetweenTasks:
        return this.getJobsBetweenTasksData(config)
      case ChartDataSource.Cashflow:
        return this.getCashflowData(config)
      case ChartDataSource.ForecastedTasks:
      case ChartDataSource.ForecastedTasksInProgress:
        return this.getForecastedTasksData(config)
      case ChartDataSource.Extras:
        return this.getExtras(config)
      default:
        this.logger.log('chart-datasource-provider', 'provideData', 'data source not implemented')
        throw new Error('data source not implemented')
    }
  }

  private getExtras(config: ChartConfig): Observable<ChartResult> {
    const obs$ = this.extrasApi.getExtras()
    return this.getData(config, obs$)
  }

  /** metrics show a single (optionally split) aggregated value for the whole (filtered) dataset.
   *  so this bypasses groupData() entirely */
  provideMetricData(config: ChartConfig): Observable<MetricResult> {
    if (config.dataSourceId === ChartDataSource.TaskDurations) {
      return this.getTaskDurationMetricData(config)
    }

    if (
      config.dataSourceId === ChartDataSource.ForecastedTasks ||
      config.dataSourceId === ChartDataSource.ForecastedTasksInProgress
    ) {
      return this.provideForecastMetricData(config)
    }

    let splitLabels: string[] | undefined
    return this.getMetricSourceItems(config).pipe(
      tap((items) => {
        if (this.utils.isMultipleSeries(config) && !config.showFilteredCategoriesOnly) {
          splitLabels = Array.from(new Set(items.map((item) => this.getMetricSplitLabel(item, config))))
        }
      }),
      map((items) => this.filterDates(config, items)),
      map((filtered) => filtered.items.filter((i) => !filtered.filteredIds.includes(i.id))),
      mergeMap((items) => (config.filters ? this.filterMetricItems(config, items) : of(items))),
      mergeMap((items) =>
        config.excludeInactiveUsers ? this.filterInactiveUsersArray(config, items) : of(items)
      ),
      map((items) => this.aggregateMetricValue(items, config, splitLabels))
    )
  }

  private provideForecastMetricData(config: ChartConfig): Observable<MetricResult> {
    return this.getForecastDataContext(config).pipe(
      mergeMap(({ task1Items, task2Items, itemValueFunc }) => {
        const splitLabels =
          this.utils.isMultipleSeries(config) && !config.showFilteredCategoriesOnly
            ? Array.from(new Set(task1Items.map((item) => this.getMetricSplitLabel(item, config))))
            : undefined
        const dateFilteredTask1Items =
          config.dataSourceId === ChartDataSource.ForecastedTasksInProgress
            ? task1Items
            : this.getMetricDateFilteredItems(config, task1Items)
        const filteredTask2$ = config.filters
          ? this.filterMetricItems(config, task2Items)
          : of(task2Items)

        return filteredTask2$.pipe(
          mergeMap((filteredTask2Items) => {
            const filteredTask1$ = config.filters
              ? this.filterMetricItems(config, dateFilteredTask1Items)
              : of(dateFilteredTask1Items)

            return filteredTask1$.pipe(
              mergeMap((items) =>
                config.excludeInactiveUsers
                  ? this.filterInactiveUsersArray(config, items)
                  : of(items)
              ),
              map((items) => {
                const metricItems =
                  config.dataSourceId === ChartDataSource.ForecastedTasksInProgress
                    ? this.getOverlappingForecastItems(items, filteredTask2Items, config)
                    : items

                return this.aggregateMetricValue(
                  metricItems,
                  config,
                  splitLabels,
                  itemValueFunc
                )
              })
            )
          })
        )
      })
    )
  }

  private getForecastDataContext(config: ChartConfig): Observable<ForecastDataContext> {
    const recalc$ = this.cashFlowApi.getRecalcCompletion()
    const jobs$: Observable<ProcessedDashboardJob[]> =
      config.valueField === CONTRACT_TOTAL_EXC_GST_SENTINEL ||
      config.valueField === CONTRACT_PRICE_SENTINEL
        ? this.jobsApi.getJobs()
        : of([])
    const jobFields$ =
      config.valueField === TRACKING_VALUE_FIELD_SENTINEL && config.trackingField
        ? this.jobFieldsApi.getJobFieldValues(config.trackingField)
        : of([])

    return zip(recalc$, jobs$, jobFields$).pipe(
      map(([cashflow, jobs, jobFields]) => ({
        task1Items: cashflow.filter((item) => item.taskMasterId === config.taskMasterId),
        task2Items: cashflow.filter((item) => item.taskMasterId === config.taskMaster2Id),
        itemValueFunc: this.getForecastItemValueFunc(config, jobs, jobFields),
      }))
    )
  }

  private getForecastItemValueFunc(
    config: ChartConfig,
    jobs: ProcessedDashboardJob[],
    jobFields: Parameters<typeof buildForecastTasksItemValueFunc>[0]
  ): ItemValueFunc | undefined {
    if (config.valueField === TRACKING_VALUE_FIELD_SENTINEL && config.trackingField) {
      return buildForecastTasksItemValueFunc(jobFields)
    }
    if (config.valueField === CONTRACT_TOTAL_EXC_GST_SENTINEL) {
      return buildJobFieldValueFunc(jobs, 'contractTotalExcGST')
    }
    if (config.valueField === CONTRACT_PRICE_SENTINEL) {
      return buildJobFieldValueFunc(jobs, 'contractPrice')
    }
    return undefined
  }

  private getMetricDateFilteredItems(
    config: ChartConfig,
    items: RecalcCompletionForecast[]
  ): RecalcCompletionForecast[] {
    const filtered = this.filterDates(config, items)
    return filtered.items.filter((item) => !filtered.filteredIds.includes(item.id))
  }

  private getOverlappingForecastItems(
    task1Items: RecalcCompletionForecast[],
    task2Items: RecalcCompletionForecast[],
    config: ChartConfig
  ): RecalcCompletionForecast[] {
    return getDistinctOverlappingForecastTasks(
      task1Items,
      task2Items,
      config,
      this.notiService
    )
  }

  private getMetricSourceItems(config: ChartConfig): Observable<any[]> {
    switch (config.dataSourceId) {
      case ChartDataSource.Tasks:
        if (config.taskMasterId == null && !config.taskCode) {
          this.logger.log(
            'chart-datasource-provider',
            'getMetricSourceItems',
            'no task master id or task code in metric config for tasks datasource'
          )
          throw new Error('no task master id or task code in metric config for tasks datasource')
        }
        return config.taskCode
          ? this.tasksApi.getTasksForTaskCode(config.taskCode)
          : this.tasksApi.getTasksForTaskmaster(config.taskMasterId as number)
      case ChartDataSource.Claims:
        return this.claimsApi.getClaims()
      case ChartDataSource.Jobs:
        return this.jobsApi.getJobs()
      case ChartDataSource.JobsBetweenTasks:
        return this.getJobsBetweenTasks(config)
      case ChartDataSource.Cashflow:
        return this.cashFlowApi.getCashflow()
      case ChartDataSource.Extras:
        return this.extrasApi.getExtras()
      default:
        this.logger.log(
          'chart-datasource-provider',
          'getMetricSourceItems',
          'data source not supported for metrics'
        )
        throw new Error('data source not supported for metrics')
    }
  }

  private filterMetricItems(config: ChartConfig, items: any[]): Observable<any[]> {
    let dxFilter = config.filters ? JSON.parse(config.filters) : undefined
    if (dxFilter) {
      dxFilter = UtilsService.patchIsBlankFilter(dxFilter)
    }

    const datasource = new DataSource({
      store: items,
      paginate: false,
      filter: dxFilter,
    })
    datasource.load()

    return of(datasource.items())
  }

  private aggregateMetricValue(
    items: any[],
    config: ChartConfig,
    splitLabels?: string[],
    itemValueFunc?: ItemValueFunc
  ): MetricResult {
    const splitValues = this.utils.isMultipleSeries(config)
      ? this.aggregateMetricSplitValues(items, config, splitLabels, itemValueFunc)
      : undefined
    const isCount = UtilsService.chartValueAggregationIsCount(config)
    const contributingItems = isCount || itemValueFunc
      ? items
      : items.filter((item) => {
          const value = item[config.valueField]
          return value != null && value !== '' && Number.isFinite(Number(value))
        })
    const numItems = contributingItems.length
    const jobIdField = this.datasourceConfig.getJobIdField(config.dataSourceId)
    const jobIds = Array.from(
      new Set(
        contributingItems
          .map((item) => item[jobIdField])
          .filter((jobId) => jobId != null && jobId !== '')
      )
    ).sort()

    if (isCount) {
      return { value: splitValues?.[0]?.value ?? numItems, numItems, jobIds, splitValues }
    }

    const getItemValue = ChartDatasourceHelper.buildItemValueFunc(config, itemValueFunc)
    const sum = contributingItems.reduce((total, item) =>
      total + getItemValue(item, jobIdField), 0)
    const value =
      config.valueAggregationTypeId === ValueAggregationType.Average && numItems
        ? this.utils.round(sum / numItems, AVERAGING_DECIMAL_PLACES_ROUNDING)
        : sum

    return { value: splitValues?.[0]?.value ?? value, numItems, jobIds, splitValues }
  }

  private aggregateMetricSplitValues(
    items: any[],
    config: ChartConfig,
    splitLabels?: string[],
    itemValueFunc?: ItemValueFunc
  ): MetricValue[] {
    const groups = new Map<string, any[]>()
    splitLabels?.forEach((label) => groups.set(label, []))
    for (const item of items) {
      const label = this.getMetricSplitLabel(item, config)
      const group = groups.get(label) ?? []
      group.push(item)
      groups.set(label, group)
    }

    const jobIdField = this.datasourceConfig.getJobIdField(config.dataSourceId)
    const getItemValue = ChartDatasourceHelper.buildItemValueFunc(config, itemValueFunc)
    return Array.from(groups, ([label, group]) => {
      const isCount = UtilsService.chartValueAggregationIsCount(config)
      const contributing = isCount || itemValueFunc
        ? group
        : group.filter((item) => item[config.valueField] != null && item[config.valueField] !== '' && Number.isFinite(Number(item[config.valueField])))
      const sum = contributing.reduce(
        (total, item) => total + getItemValue(item, jobIdField),
        0
      )
      const value = config.valueAggregationTypeId === ValueAggregationType.Average && contributing.length
        ? this.utils.round(sum / contributing.length, AVERAGING_DECIMAL_PLACES_ROUNDING)
        : sum
      const jobIds = Array.from(new Set(contributing.map((item) => item[jobIdField]).filter((id) => id != null && id !== ''))).sort()
      return { label, value, numItems: contributing.length, jobIds }
    })
  }

  private getMetricSplitLabel(item: any, config: ChartConfig): string {
    const rawLabel = item[config.splitByField]
    return rawLabel == null || rawLabel === '' ? SERIES_EMPTY_TEXT : String(rawLabel)
  }

  private getForecastedTasksData(config: ChartConfig): Observable<ChartResult> {
    return this.getForecastDataContext(config).pipe(
      mergeMap(({ task1Items, task2Items, itemValueFunc }) => {
        // apply the custom filters on the task2Items
        // date filtering applied in FORECAST_TASK_IN_PROGRESS_GROUP_KEY_FUNC (same with task1 items)
        if (config.filters) {
          return this.filter(config, { items: task2Items, filteredIds: [] }).pipe(
            map((filtered) => {
              this.groupKeyFuncOverrideItems = filtered.items
              return [task1Items, itemValueFunc] as const
            })
          )
        } else {
          this.groupKeyFuncOverrideItems = task2Items
          return of([task1Items, itemValueFunc] as const)
        }
      }),
      mergeMap(([filteredTask1Items, itemValueFunc]) =>
        this.getData(
          config,
          of(filteredTask1Items),
          itemValueFunc,
          config.dataSourceId === ChartDataSource.ForecastedTasksInProgress
        ).pipe(
          tap(() => {
            this.groupKeyFuncOverrideItems = []
          })
        )
      )
    )
  }

  private getCashflowData(config: ChartConfig): Observable<ChartResult> {
    const obs$ = this.cashFlowApi.getCashflow()

    return this.getData(config, obs$)
  }

  private getJobsData(config: ChartConfig): Observable<ChartResult> {
    const obs$ = this.jobsApi.getJobs()

    // 'currentActivity' has a custom (non-alphabetical) display order - resolved fresh per
    // request from ActivitiesApiService (itself cached per-company)
    const needsActivityRank =
      config.argumentField === 'currentActivity' || config.splitByField === 'currentActivity'

      // TODO: put activity order on job DTO to avoid call to activities endpoint
    const fieldRankMaps$: Observable<FieldRankMaps | undefined> = needsActivityRank
      ? this.activitiesApi.getActivities(true).pipe(
          map((activities) => {
            const fieldRankMaps: FieldRankMaps = new Map()
            fieldRankMaps.set(
              'currentActivity',
              ChartDatasourceHelper.buildRankMap(activities.map((a) => a.description))
            )
            return fieldRankMaps
          })
        )
      : of(undefined)

    return fieldRankMaps$.pipe(
      mergeMap((fieldRankMaps) => this.getData(config, obs$, undefined, false, fieldRankMaps))
    )
  }

  private getJobsBetweenTasksData(config: ChartConfig): Observable<ChartResult> {
    return this.getData(config, this.getJobsBetweenTasks(config))
  }

  private getJobsBetweenTasks(config: ChartConfig): Observable<ProcessedDashboardJob[]> {
    if (config.taskMasterId == null || config.taskMaster2Id == null) {
      throw new Error('both task master ids are required for Jobs between Tasks datasource')
    }

    return zip(
      this.reportsApi.getJobsBetweenTasks(config.taskMasterId, config.taskMaster2Id),
      this.jobsApi.getJobs()
    ).pipe(
      map(([jobNumbers, jobs]) => {
        const matchingJobNumbers = new Set(jobNumbers)
        return jobs.filter((job) => matchingJobNumbers.has(job.jobNumber))
      })
    )
  }

  private getClaimsData(config: ChartConfig): Observable<ChartResult> {
    const obs$ = this.claimsApi.getClaims()

    return this.getData(config, obs$)
  }

  private getTasksData(config: ChartConfig): Observable<ChartResult> {
    if (config.taskMasterId == null && !config.taskCode) {
      this.logger.log(
        'chart-datasource-provider',
        'getTasksData',
        'no task master id or task code in chart config for tasks datasource'
      )
      throw new Error('no task master id or task code in chart config for tasks datasource')
    }

    const obs$ = config.taskCode
      ? this.tasksApi.getTasksForTaskCode(config.taskCode)
      : this.tasksApi.getTasksForTaskmaster(config.taskMasterId as number)

    return this.getData(config, obs$)
  }

  private getTaskDurationData(config: ChartConfig): Observable<ChartResult> {
    return this.getTaskDurationSource(config).pipe(
      mergeMap(([tasks1, tasks2]) => {
        return this.tasksApi.getTaskMastersForCurrentCompany().pipe(
          map((tasks) => {
            const task1Name = tasks.filter((t) => t.id === config.taskMasterId)[0]?.taskTitle
            const task2Name = tasks.filter((t) => t.id === config.taskMaster2Id)[0]?.taskTitle
            return [tasks1, tasks2, task1Name, task2Name] as const
          })
        )
      }),
      map(([tasks1, tasks2, task1Name, task2Name]) =>
        this.taskDurationService.processTaskDurations(tasks1, tasks2, task1Name, task2Name, config)
      )
    )
  }

  private getTaskDurationMetricData(config: ChartConfig): Observable<MetricResult> {
    return this.getTaskDurationSource(config).pipe(
      map(([tasks1, tasks2]) =>
        this.taskDurationService.processTaskDurationMetric(tasks1, tasks2, config)
      )
    )
  }

  private getTaskDurationSource(
    config: ChartConfig
  ): Observable<[ProcessedJobTask[], ProcessedJobTask[]]> {
    if (config.taskMasterId == null || config.taskMaster2Id == null) {
      this.logger.log(
        'chart-datasource-provider',
        'getTaskDurationSource',
        'no task master ids in config for task-duration datasource'
      )
      throw new Error('no task master ids in config for task-duration datasource')
    }

    let obs$: Observable<[ProcessedJobTask[], FilteredItems]> = zip(
      this.tasksApi.getTasksForTaskmaster(config.taskMasterId),
      this.tasksApi.getTasksForTaskmaster(config.taskMaster2Id)
    ).pipe(map(([tasks1, tasks2]) => [tasks1, this.filterDates(config, tasks2)]))

    if (config.filters) {
      obs$ = obs$.pipe(
        mergeMap(([tasks1, tasks2]) =>
          this.filter(config, tasks2).pipe(
            map((filtered) => [tasks1, filtered] as [ProcessedJobTask[], FilteredItems])
          )
        )
      )
    }

    // remove unused filtered item info
    let tasks$: Observable<[ProcessedJobTask[], ProcessedJobTask[]]> = obs$.pipe(
      map(([tasks1, tasks2]) => [tasks1, tasks2.items] as [ProcessedJobTask[], ProcessedJobTask[]])
    )

    if (config.excludeInactiveUsers) {
      tasks$ = tasks$.pipe(
        mergeMap(([tasks1, tasks2]) =>
          this.filterInactiveUsersArray(config, tasks1).pipe(
            mergeMap(filteredTasks1 =>
              this.filterInactiveUsersArray(config, tasks2).pipe(
                map(filteredTasks2 => [filteredTasks1, filteredTasks2] as [ProcessedJobTask[], ProcessedJobTask[]])
              )
            )
          )
        )
      )
    }

    return tasks$
  }

  private getData(
    config: ChartConfig,
    obs$: Observable<any>,
    itemValueFunc?: ItemValueFunc,
    skipItemDateFiltering = false,
    fieldRankMaps?: FieldRankMaps
  ): Observable<ChartResult> {
    const filtered$ = obs$.pipe(
      mergeMap((res) => {
        console.groupCollapsed(`[Chart Filter] ${config.cardTitle}`)
        return of(res).pipe(
          map((items) => this.filterDates(config, items, skipItemDateFiltering)),
          mergeMap((filtered) =>
            config.filters ? this.filter(config, filtered) : of(filtered)
          ),
          mergeMap((filtered) =>
            config.excludeInactiveUsers
              ? this.filterInactiveUsers(config, filtered)
              : of(filtered)
          ),
          map((filtered) => {
            console.groupEnd()
            return filtered
          }),
          catchError((err) => {
            console.groupEnd()
            return throwError(() => err)
          })
        )
      })
    )

    return filtered$.pipe(
      mergeMap((res) => {
        console.groupCollapsed(`[Chart GroupData] ${config.cardTitle}`)
        try {
          return of(this.groupData(res, config, itemValueFunc, fieldRankMaps)).pipe(
            map((result) => {
              console.groupEnd()
              return result
            })
          )
        } catch (err) {
          console.groupEnd()
          return throwError(() => err)
        }
      })
    )
  }

  /** use devextreme datasource to filter based on custom rules */
  private filter(config: ChartConfig, filteredItems: FilteredItems): Observable<FilteredItems> {
    let dxFilter = config.filters ? JSON.parse(config.filters) : undefined
    if (dxFilter) {
      dxFilter = UtilsService.patchIsBlankFilter(dxFilter)
    }

    const datasource = new DataSource({
      store: filteredItems.items,
      paginate: false,
      filter: dxFilter,
    })
    datasource.load()
    console.debug(
      config.cardTitle + ' >> num items passing custom filters ' + datasource.items().length
    )
    console.debug(datasource.items().sort((a, b) => a.id - b.id))

    if (this.utils.showFilteredCategoriesOnly(config))
      return of({
        filteredIds: [],
        items: datasource.items(),
        dateFrom: filteredItems.dateFrom,
        dateTo: filteredItems.dateTo
      })

    return from(datasource.store().load()).pipe(
      map((allItems: any) => {
        // inefficient =^_^=
        // hopefully way for dx-datasource to mark filtered items instead of removing them
        // or can implement the filtering ourselves
        const passedFilter = datasource.items()
        allItems.forEach((i: any) => {
          if (passedFilter.indexOf(i) < 0) filteredItems.filteredIds.push(i.id)

          return i
        })
        filteredItems.filteredIds = [...new Set(filteredItems.filteredIds)]
        console.debug(config.cardTitle + ' >> items passing date and custom filters:')
        console.debug(filteredItems.items.filter((i) => !filteredItems.filteredIds.includes(i.id)))
        return filteredItems
      })
    )
  }

  /** Returns an Observable of inactive user names + person fields, or null if no person fields apply */
  private getInactiveUserContext(config: ChartConfig): { personFields: string[], inactiveNames$: Observable<Set<string>> } | null {
    const personFields = this.datasourceConfig.getPersonFieldsInConfig(
      config.dataSourceId, config.argumentField, config.splitByField
    )
    if (personFields.length === 0) return null

    const inactiveNames$ = this.userService.getCurrCompUsers(undefined).pipe(
      map(users => ChartDatasourceHelper.buildInactiveNameSet(users))
    )
    return { personFields, inactiveNames$ }
  }

  /** Remove items where a person-type field (argument or splitBy) matches an inactive user */
  private filterInactiveUsers(config: ChartConfig, filteredItems: FilteredItems): Observable<FilteredItems> {
    const ctx = this.getInactiveUserContext(config)
    if (!ctx) return of(filteredItems)

    return ctx.inactiveNames$.pipe(
      map(inactiveNames => ChartDatasourceHelper.applyInactiveFilter(filteredItems, inactiveNames, ctx.personFields))
    )
  }

  /** Simpler variant that works on a plain array (used by task-duration flow) */
  private filterInactiveUsersArray(config: ChartConfig, items: any[]): Observable<any[]> {
    const ctx = this.getInactiveUserContext(config)
    if (!ctx) return of(items)

    return ctx.inactiveNames$.pipe(
      map(inactiveNames => ChartDatasourceHelper.removeInactiveItems(items, inactiveNames, ctx.personFields))
    )
  }

  private filterDates(
    config: ChartConfig,
    items: any[],
    skipItemDateFiltering = false //a bounded range still exists, but filtering happens elsewhere
  ): FilteredItems {
    console.debug(config.cardTitle + ' >> num items pre filters ' + items.length)
    if (config.dateRangeTypeId == null) {
      throw Error('tried to filter dates without a date filter field or date range type')
    }

    if (config.dateRangeTypeId === DateRangeType['All Time']) {
      return { filteredIds: [], items, dateFrom: null, dateTo: null }
    }

    if (!skipItemDateFiltering && !config.dateFilterField) {
      throw Error('tried to filter dates without a date filter field or date range type')
    }

    let startWindow: dayjs.Dayjs | null = null
    let endWindow: dayjs.Dayjs | null = null

    switch (config.dateRangeTypeId) {
      case DateRangeType.Custom:
        startWindow = DateUtilsService.getDayjsUTCDateWithoutTimeFromDate(config.dateFrom)
        endWindow = DateUtilsService.getDayjsUTCDateWithoutTimeFromDate(config.dateTo)
        break
      default: {
        const dates = DateUtilsService.getGroupingStartAndEndDate(
          config.dateRangeTypeId,
          config.includeCurrentPartialPeriod,
          config.dateAxisTypeId,
          config.argumentFieldIsDate
        )
        startWindow = dates[0]
        endWindow = dates[1]
        break
      }
    }

    // reduce start date by MAX_ROLLING_PERIODS to allow for rolling average calcs - removed later
    const specifiedStart = startWindow
    if (this.utils.chartUsingRollingPeriod(config) && config.argumentFieldIsDate) {
      const periodGrouping = this.dateUtils.getDateGroupingPeriodName(config.dateAxisTypeId)
      startWindow = startWindow?.subtract(MAX_ROLLING_PERIODS, periodGrouping) ?? null
    }

    console.debug(
      'Filter Dates (UTC):',
      startWindow?.format(),
      endWindow?.format(),
      'Specified Start:',
      specifiedStart?.format()
    )

    // Keep dateFrom/dateTo for axis padding/range bounds
    if (skipItemDateFiltering) {
      return {
        filteredIds: [],
        items,
        dateFrom: specifiedStart,
        dateTo: endWindow,
      }
    }

    if (this.utils.showFilteredCategoriesOnly(config)) {
      const res = {
        filteredIds: [],
        items: items.filter((i) => {
          const itemDate = DateUtilsService.getDayjsUTCDateWithoutTimeFromDate(i[config.dateFilterField!])
          //console.debug(itemDate.format())
          if (!itemDate) return config.includeRecordsWithNoDate === true
          return (
            !startWindow ||
            (startWindow.isSameOrBefore(itemDate) &&
              (!endWindow || itemDate.isSameOrBefore(endWindow)))
          )
        }),
        dateFrom: specifiedStart,
        dateTo: endWindow,
      }
      console.debug(config.cardTitle + ' >> num items after filter dates: ' + res.items.length)
      console.debug(res.items.sort((a, b) => a.id - b.id))
      return res
    }

    const filteredIds: number[] = []
    items.forEach((i) => {
      const itemDate = DateUtilsService.getDayjsUTCDateWithoutTimeFromDate(i[config.dateFilterField!])
      //console.debug(itemDate?.format())
      const filteredOut = !itemDate
        ? !config.includeRecordsWithNoDate
        : (startWindow! && !startWindow.isSameOrBefore(itemDate)) ||
          (endWindow! && !itemDate.isSameOrBefore(endWindow))
      if (filteredOut) filteredIds.push(i.id)
    })

    console.debug(
      config.cardTitle +
        ` >> num items passing filter dates: ${items.length - filteredIds.length} (${
          filteredIds.length
        } filtered out)`
    )
    console.debug(items.filter((i) => !filteredIds.includes(i.id)).sort((a, b) => a.id - b.id))

    return {
      filteredIds: filteredIds,
      items: items,
      dateFrom: specifiedStart,
      dateTo: endWindow,
    }
  }

  private groupData(
    filteredItems: FilteredItems,
    config: ChartConfig,
    itemValueFunc?: ItemValueFunc,
    fieldRankMaps?: FieldRankMaps
  ): ChartResult {
    const grpField = config.argumentField

    if (config.argumentFieldIsDate) {
      ChartDatasourceHelper.convertAndOrderDates(filteredItems.items, grpField)
    } else {
      filteredItems.items.sort(
        this.utils.sortByFieldOrCustomRank(grpField, fieldRankMaps?.get(grpField))
      )
    }

    const groupKeyFunc =
      this.datasourceConfig.datasourceCustomGroupKeyFunc(config.dataSourceId) ??
      this.dateUtils.getDatasourceProviderGroupKeyFunc(
        config.argumentFieldIsDate,
        config.dateAxisTypeId
      )

    const jobIdField = this.datasourceConfig.getJobIdField(config.dataSourceId)

    const isCount = UtilsService.chartValueAggregationIsCount(config)
    const getItemValue = ChartDatasourceHelper.buildItemValueFunc(config, itemValueFunc)

    const formatDateFunc = this.dateUtils.formatDatePadded

    const [splittingByDateField, isMultipleSeries, chartSeries] = this.utils.createChartSeries(
      filteredItems,
      config,
      formatDateFunc,
      fieldRankMaps?.get(config.splitByField)
    )

    const warningCollector = config.dataSourceId === ChartDataSource.ForecastedTasksInProgress
      ? new ForecastIntervalWarningCollector(this.notiService, config.cardTitle)
      : undefined
    let grouped = filteredItems.items.reduce((previous: GenericChartItem[], current) => {
      return this.reduceItem(
        filteredItems.items,
        previous,
        current,
        groupKeyFunc,
        filteredItems.filteredIds,
        config,
        jobIdField,
        splittingByDateField,
        chartSeries,
        formatDateFunc,
        getItemValue,
        warningCollector ?? this.notiService
      )
    }, [])
    warningCollector?.flush()

    grouped.forEach((g) => {
      g.jobIds.sort()
    })

    if (config.argumentFieldIsDate)
      grouped = this.fillInDatePeriodsWithZeroValues(
        grouped,
        config,
        chartSeries,
        filteredItems.dateFrom,
        filteredItems.dateTo
      )

    if (!isCount && config.valueAggregationTypeId === ValueAggregationType.Average)
      this.averageValues(grouped, isMultipleSeries, chartSeries)
    else if (
      config.valueAggregationTypeId === ValueAggregationType['Average - Rolling 3 Period'] ||
      config.valueAggregationTypeId === ValueAggregationType['Average - Rolling 6 Period']
    )
      this.periodAverageValues(grouped, isMultipleSeries, chartSeries, config)
    else if (config.valueAggregationTypeId === ValueAggregationType.Cumulative)
      this.cumulateValues(grouped, isMultipleSeries, chartSeries, config)
    else if (config.valueAggregationTypeId === ValueAggregationType.Sum)
      if (!UtilsService.chartValueAggregationIsCount(config))
        grouped.forEach((g) => {
          g['additionalPointInfoValues'] = [
            {
              label: 'Average for current grouping',
              value: this.getAverageValueOfChartItem(g),
              isNumber: true,
              notBold: true,
            },
          ]
        })

    // remove extra periods from start that were added for rolling averages
    if (this.utils.chartUsingRollingPeriod(config) && config.argumentFieldIsDate) {
      // assuming rolling period charts cannot have dateFrom that doesn't align to period grouping
      grouped = grouped.filter((g) => {
        const d = DateUtilsService.getDayjsUTCDateWithoutTimeFromDate(g.groupField)
        const configDate = DateUtilsService.getDayjsUTCDateWithoutTimeFromDate(filteredItems.dateFrom)
        return !filteredItems.dateFrom || (!!d && d.isSameOrAfter(configDate))
      })
    }

    console.debug(config.cardTitle + ' grouped: ')
    console.debug(grouped)
    //console.debug(chartSeries)

    const hasDynamicTarget = ChartDatasourceHelper.buildTargetData(grouped, config)

    return { data: grouped, splitSeries: isMultipleSeries, dynamicSeries: chartSeries, hasDynamicTarget }
  }

  private reduceItem(
    allItems: any[],
    previousItems: GenericChartItem[],
    currentItem: any,
    groupKeyFunc: GROUP_KEYS_FUNCTION<any>,
    filteredIds: (number | string)[],
    config: ChartConfig,
    jobIdField: string,
    splittingByDateField: boolean,
    chartSeries: ChartValue[],
    formatDateFunc: (d: any) => string,
    getItemValue: ItemValueFunc,
    warningNotifier: WarningNotifier
  ): GenericChartItem[] {
    if (!currentItem[config.argumentField]) {
      if (!config.argumentFieldIsDate)
        console.warn('undefined argument field for non-date item - currently ignoring')
      return previousItems // group as 'no data' instead of ignoring? (if argument is not date)
    }

    const isFilteredOut = filteredIds.indexOf(currentItem.id) > -1
    // date groupings will be 0-padded in fillInDatePeriodsWithZeroValues()
    if (isFilteredOut && config.argumentFieldIsDate) return previousItems

    const groupKeys = groupKeyFunc(
      this.groupKeyFuncOverrideItems.length ? this.groupKeyFuncOverrideItems : allItems,
      currentItem,
      config,
      warningNotifier
    )

    for (let i = 0; i < groupKeys.length; i++) {
      const groupKey = groupKeys[i]

      let existingItem: GenericChartItem[]
      if (config.argumentFieldIsDate) {
        existingItem = previousItems.filter(
          (g) => g.groupField.getTime() === (groupKey as Date).getTime()
        )
      } else {
        existingItem = previousItems.filter((g) => g.groupField === groupKey)
      }

      let value = getItemValue(currentItem as Record<string, unknown>, jobIdField)
      if (isFilteredOut) value = 0

      let multipleSeriesName = this.utils.checkForNullOrEmpty(currentItem[config.splitByField])
      if (splittingByDateField) multipleSeriesName = formatDateFunc(multipleSeriesName)

      if (existingItem.length === 1) {
        const existingItemSingle = existingItem[0]
        existingItemSingle.value += value
        if (!isFilteredOut) existingItemSingle.numItems++

        if (!existingItemSingle[multipleSeriesName]) existingItemSingle[multipleSeriesName] = 0
        existingItemSingle[multipleSeriesName] += value

        if (!isFilteredOut) existingItemSingle.seriesNumItems[multipleSeriesName]++

        if (!isFilteredOut && !existingItemSingle.jobIds.includes(currentItem[jobIdField])) {
          existingItemSingle.jobIds.push(currentItem[jobIdField])
        }
      } else {
        const item = {
          groupField: groupKey,
          numItems: isFilteredOut ? 0 : 1,
          value,
          jobIds: isFilteredOut ? [] : [currentItem[jobIdField]],
        } as GenericChartItem

        ChartDatasourceHelper.initChartItemZeroValues(item, chartSeries)

        item[multipleSeriesName] = value
        item.seriesNumItems[multipleSeriesName] = isFilteredOut ? 0 : 1

        previousItems.push(item)
      }
    }

    return previousItems
  }

  private averageValues(
    grouped: GenericChartItem[],
    isMultipleSeries: boolean,
    chartSeries: ChartSeries[]
  ) {
    grouped.forEach((g) => {
      if (isMultipleSeries) {
        chartSeries.forEach((series) => {
          g[series.value] = this.utils.round(
            g[series.value] / g.seriesNumItems[series.value],
            AVERAGING_DECIMAL_PLACES_ROUNDING
          )
        })
      }

      g['additionalPointInfoValues'] = [
        { label: 'Sum for current grouping', value: g.value, isNumber: true, notBold: true },
      ]

      g.value = this.getAverageValueOfChartItem(g)
    })
  }

  private periodAverageValues(
    grouped: GenericChartItem[],
    isMultipleSeries: boolean,
    chartSeries: ChartSeries[],
    config: ChartConfig
  ) {
    const windowSize =
      config.valueAggregationTypeId === ValueAggregationType['Average - Rolling 3 Period'] ? 3 : 6

    const originalGrouped = grouped.map((g) => ({ ...g }))
    this.averageValues(originalGrouped, isMultipleSeries, chartSeries)

    const movingAverage = (arr: any[], idx: number, prop: string, window: number) => {
      if (idx === 0) {
        // First value remains unchanged
        return arr[0][prop] || 0
      }
      // For idx > 0, average from 0 to idx (or up to window size)
      const start = Math.max(0, idx - window + 1)
      const windowItems = arr.slice(start, idx + 1)
      const sum = windowItems.reduce((acc, item) => acc + (item[prop] || 0), 0)
      return this.utils.round(sum / windowItems.length, AVERAGING_DECIMAL_PLACES_ROUNDING)
    }

    grouped.forEach((g, i) => {
      if (isMultipleSeries) {
        chartSeries.forEach((series) => {
          g[series.value] = movingAverage(originalGrouped, i, series.value, windowSize)
        })
      }
      g.value = movingAverage(originalGrouped, i, 'value', windowSize)
    })
  }

  private cumulateValues(
    grouped: GenericChartItem[],
    isMultipleSeries: boolean,
    chartSeries: ChartSeries[],
    config: ChartConfig
  ) {
    let cumulativeSum = config.startingValue && !isMultipleSeries ? config.startingValue : 0
    let cumulativeNumItems = 0
    const multipleSeriesCumulativeSums: { [id: string]: number } = {}
    const multipleSeriesCumulativeNumItems: { [id: string]: number } = {}
    chartSeries.forEach((s) => (multipleSeriesCumulativeSums[s.value] = 0))
    chartSeries.forEach((s) => (multipleSeriesCumulativeNumItems[s.value] = 0))

    grouped = grouped.map((g) => {
      if (isMultipleSeries) {
        chartSeries.forEach((series) => {
          if (!g[series.value]) g[series.value] = 0
          g[series.value] += multipleSeriesCumulativeSums[series.value]
          multipleSeriesCumulativeSums[series.value] = g[series.value]
          multipleSeriesCumulativeNumItems[series.value] += g.seriesNumItems[series.value] ?? 0
        })
        g.seriesCumulativeNumItems = { ...multipleSeriesCumulativeNumItems }
      }

      cumulativeNumItems += g.numItems
      g.cumulativeNumItems = cumulativeNumItems
      g['additionalPointInfoValues'] = [
        {
          label: 'Num Items for current grouping',
          value: g.numItems,
          isNumber: true,
          notBold: true,
        },
        { label: 'Sum for current grouping', value: g.value, isNumber: true, notBold: true },
        {
          label: 'Average for current grouping',
          value: this.getAverageValueOfChartItem(g),
          isNumber: true,
          notBold: true,
        },
      ]

      g.value += cumulativeSum
      cumulativeSum = g.value

      return g
    })
  }

  /** Fill 'missing' periods with 0 values */
  private fillInDatePeriodsWithZeroValues(
    grouped: GenericChartItem[],
    config: ChartConfig,
    chartSeries: ChartValue[],
    filterDateFrom?: Dayjs | null,
    filterDateTo?: Dayjs | null
  ): GenericChartItem[] {
    if (grouped.length === 0) {
      return []
    }

    if (filterDateFrom) filterDateFrom = DateUtilsService.getDayjsUTCDateWithoutTimeFromDate(filterDateFrom)
    if (filterDateTo) filterDateTo = DateUtilsService.getDayjsUTCDateWithoutTimeFromDate(filterDateTo)

    const periodName = this.dateUtils.getDayJsPeriodName(config.dateAxisTypeId)
    const newGrouped: GenericChartItem[] = []

    const firstItemDate = DateUtilsService.getDayjsUTCDateWithoutTimeFromDate(grouped[0].groupField)

    // Add initial padding from startDate to the first item in grouped
    if (filterDateFrom && firstItemDate && filterDateFrom.isBefore(firstItemDate)) {
      const periodsToFirstItem = firstItemDate.diff(filterDateFrom, periodName)

      for (let i = 0; i < periodsToFirstItem; i++) {
        const newItem = {
          value: 0,
          groupField: filterDateFrom.add(i, periodName).toDate(),
          numItems: 0,
        } as GenericChartItem
        ChartDatasourceHelper.initChartItemZeroValues(newItem, chartSeries)
        newGrouped.push(newItem)
      }
    }

    // Add items and pad between them
    for (let i = 0; i < grouped.length; i++) {
      const currentModel = grouped[i]
      newGrouped.push(currentModel)

      const nextModel = grouped[i + 1]
      if (nextModel) {
        const currentDate = dayjs(currentModel.groupField)
        const periodsBetween = dayjs(nextModel.groupField).diff(currentDate, periodName)

        if (periodsBetween > 1) {
          for (let j = 1; j < periodsBetween; j++) {
            const newItem = {
              value: 0,
              groupField: currentDate.add(j, periodName).toDate(),
              numItems: 0,
            } as GenericChartItem
            ChartDatasourceHelper.initChartItemZeroValues(newItem, chartSeries)
            newGrouped.push(newItem)
          }
        }
      }
    }

    // add end padding from last item to filterDateTo
    if (filterDateTo && newGrouped.length > 0) {
      const lastItemDate = dayjs(newGrouped[newGrouped.length - 1].groupField)
      if (filterDateTo.isAfter(lastItemDate)) {
        const periodsFromLastItem = filterDateTo.diff(lastItemDate, periodName)
        for (let i = 1; i <= periodsFromLastItem; i++) {
          const newItem = {
            value: 0,
            groupField: lastItemDate.add(i, periodName).toDate(),
            numItems: 0,
          } as GenericChartItem
          ChartDatasourceHelper.initChartItemZeroValues(newItem, chartSeries)
          newGrouped.push(newItem)
        }
      }
    }

    return newGrouped
  }

  private getAverageValueOfChartItem(item: GenericChartItem): number {
    return item.numItems === 0
      ? 0
      : this.utils.round(item.value / item.numItems, AVERAGING_DECIMAL_PLACES_ROUNDING)
  }
}
